import { UseQueryResult, useQuery } from '@tanstack/react-query';
import {
  getAccountImageTypes,
  getAccountImages,
  getAccountUIThemeById,
  getMyAccountById,
  getMyAccountDetail,
  getMyAccountDetailsSubscriptions,
  getProductLicenceScopes,
  getProductLicenceTypes
} from 'src/services/account';
import {
  AccountDetailsData,
  AccountInviteDetails,
  DefaultNotificationsDetails,
  ForgottenPasswordDetails,
  UserInviteDetails
} from './types';
import { getProducts } from 'src/services/tenantadmin';
import { getMessageTypes, getMessagingServices } from 'src/services/messaging';
import { Products } from 'src/@core/utils/getProductLevel';
import {
  getActionProperties,
  getActions,
  getDefaultEventActionInputs,
  getDefaultEventActionTemplates,
  getDtoProperties,
  getEventActionTemplates,
  getEventActions,
  getEventDataSources,
  getEventProperties,
  getEvents,
  getPropertyTypes,
  getRuleProfileCategories,
  getRuleProfiles,
  getRules,
  getServices,
  getTriggerTypes
} from 'src/services/automation';
import { DtoPropertyDto, RuleDto, RuleProfileDto } from 'src/models/automation';
import {
  ActionMetadata,
  ActionPropertyMetadata,
  DataSourceDtoProperty,
  DataSourceMetadata,
  EventMetadata,
  Template
} from '../notifications/types';
import { getJeopardyStatusConfigurations } from 'src/services/salesorder';
import { JeopardyStatusConfigurationDto } from 'src/models/salesorder';
import { QueryProps } from 'src/queries/baseQueryFunctions';
import { AxiosResponse } from 'axios';
import {
  AccountDetailDto,
  AccountImageDto,
  ProductLicenceScopeDto,
  ProductLicenceTypeDto,
  SubscriptionDto,
  UithemeDto
} from 'src/models/account';
import { MessagingServiceDto } from 'src/models/messaging';
import { getAllUsersAsync, getUserStates } from 'src/services/identity';
import { getDeviceAppRegistrations } from 'src/services/devicemanagement';
import { UserListItemDto } from 'src/models/identity';
import { DeviceAppRegistrationDto } from 'src/models/devicemanagement';
import { ViewDto } from '../util/types';

const accountDetailsNotificationsQuery = async (accountId: string): Promise<DefaultNotificationsDetails> => {
  let ruleProfiles: RuleProfileDto[] = [];
  let rules: RuleDto[] = [];
  let emailActionId = '';

  const ruleProfileCategories = (
    await getRuleProfileCategories({ filter: `isUserNotification eq true or isAccountNotification eq true` })
  ).data;

  if (ruleProfileCategories.length > 0) {
    let ruleProfileCategoriesFilterStr = '';
    for (let rpcCatIndex = 0; rpcCatIndex < ruleProfileCategories.length; rpcCatIndex++) {
      const rpc = ruleProfileCategories[rpcCatIndex];

      if (ruleProfileCategoriesFilterStr === '') {
        ruleProfileCategoriesFilterStr = `ruleProfileCategoryId eq guid'${rpc.id}'`;
      } else {
        ruleProfileCategoriesFilterStr = `${ruleProfileCategoriesFilterStr} or ruleProfileCategoryId eq guid'${rpc.id}'`;
      }
    }

    ruleProfiles = (await getRuleProfiles({ filter: ruleProfileCategoriesFilterStr })).data;
  }

  if (ruleProfiles.length > 0) {
    let rulesFilterStr = '';
    for (let rpIndex = 0; rpIndex < ruleProfiles.length; rpIndex++) {
      const rp = ruleProfiles[rpIndex];

      if (rulesFilterStr === '') {
        rulesFilterStr = `ruleProfileId eq guid'${rp.id}'`;
      } else {
        rulesFilterStr = `${rulesFilterStr} or ruleProfileId eq guid'${rp.id}'`;
      }
    }

    rules = (await getRules({ filter: rulesFilterStr })).data;
  }

  // Automation Metadata
  const services = getServices({ filter: `name eq identity`, ignoreOwner: true });
  const events = getEvents({ ignoreOwner: true });
  const eventProperties = getEventProperties({ ignoreOwner: true });
  const eventActions = getEventActions({ ignoreOwner: true });
  const eventDataSources = getEventDataSources({ ignoreOwner: true });
  const actions = getActions({ ignoreOwner: true });
  const actionProperties = getActionProperties({ ignoreOwner: true });
  const defaultEventActionInputs = getDefaultEventActionInputs({ ignoreOwner: true });
  const triggerTypes = getTriggerTypes();
  const propertyTypes = getPropertyTypes({ ignoreOwner: true });

  const metadataRes = await Promise.all([
    events,
    eventProperties,
    eventActions,
    eventDataSources,
    actions,
    actionProperties,
    defaultEventActionInputs,
    triggerTypes,
    propertyTypes,
    services
  ]);

  const eventMetadata: EventMetadata[] = [];
  const salesOrderService = metadataRes[9].data[0];
  for (let eventIndex = 0; eventIndex < metadataRes[0].data.length; eventIndex++) {
    if (metadataRes[0].data[eventIndex].serviceId !== salesOrderService.id) continue; // Only want Order Notifications

    const allowableActions: ActionMetadata[] = [];
    const dataSources: DataSourceMetadata[] = [];

    const eventDto = metadataRes[0].data[eventIndex];
    const eventPropertyDtos = metadataRes[1].data.filter(prop => prop.eventId === eventDto.id);
    const eventActionDtos = metadataRes[2].data.filter(eventAction => eventAction.eventId === eventDto.id);
    const eventDataSourceDtos = metadataRes[3].data.filter(x => x.eventId === eventDto.id);

    for (let eventActionsIndex = 0; eventActionsIndex < eventActionDtos.length; eventActionsIndex++) {
      const eventActionDto = eventActionDtos[eventActionsIndex];
      const actionDto = metadataRes[4].data.find(action => action.id === eventActionDtos[eventActionsIndex].actionId);

      if (actionDto?.name?.toLowerCase() === 'sendemailnotificationaction' && emailActionId === '') {
        emailActionId = actionDto.id ?? '';
      }

      const actionPropertyDtos = metadataRes[5].data.filter(property => property.actionId === actionDto?.id);

      const actProperties: ActionPropertyMetadata[] = [];
      for (let actionPropertyIndex = 0; actionPropertyIndex < actionPropertyDtos.length; actionPropertyIndex++) {
        const actionPropertyDto = actionPropertyDtos[actionPropertyIndex];
        const defaultEventActionInputDto = metadataRes[6].data.find(
          x => x.actionPropertyId === actionPropertyDto.id && x.eventActionId === eventActionDto.id
        );

        actProperties.push({
          actionProperty: actionPropertyDto,
          defaultValue: defaultEventActionInputDto ?? {}
        });
      }

      allowableActions.push({
        action: actionDto ?? {},
        eventAction: eventActionDto,
        actionProperties: actProperties
      });
    }

    const collectionPropertyType = metadataRes[8].data.find(x => x.isCollection);
    const uniqueIdentifierPropertyType = metadataRes[8].data.find(x => x.isUniqueIdentifier);

    for (let dataSourceIndex = 0; dataSourceIndex < eventDataSourceDtos.length; dataSourceIndex++) {
      const dataSource = eventDataSourceDtos[dataSourceIndex];
      let dataSourceDtoProperties: DataSourceDtoProperty[] = [];

      if (!dataSources.find(x => x.dataSource.dtoId === dataSource.dtoId)) {
        // Fetch new properties
        const dtoProperties = (await getDtoProperties({ filter: `dtoId eq guid'${dataSource.dtoId}'` })).data;

        for (
          let dataSourceDtoPropertyIndex = 0;
          dataSourceDtoPropertyIndex < dtoProperties.length;
          dataSourceDtoPropertyIndex++
        ) {
          // Is the propertyTypeId of the DtoProperty a collection?
          let dtoPropertySubProperties: DtoPropertyDto[] = [];
          if (dtoProperties[dataSourceDtoPropertyIndex].propertyTypeId === collectionPropertyType?.id) {
            dtoPropertySubProperties = (
              await getDtoProperties({
                filter: `dtoId eq guid'${dtoProperties[dataSourceDtoPropertyIndex].propertyTypeDtoId}'`
              })
            ).data;
          }

          if (dtoProperties[dataSourceDtoPropertyIndex].propertyTypeId !== uniqueIdentifierPropertyType?.id) {
            dataSourceDtoProperties.push({
              dtoProperty: dtoProperties[dataSourceDtoPropertyIndex],
              subProperties: dtoPropertySubProperties
            });
          }
        }
      } else {
        // Use existing properties
        const existingSource = dataSources.find(x => x.dataSource.dtoId === dataSource.dtoId);
        dataSourceDtoProperties = existingSource?.properties ?? [];
      }

      dataSources.push({
        dataSource: dataSource,
        properties: dataSourceDtoProperties
      });
    }

    eventMetadata.push({
      event: eventDto,
      eventProperties: eventPropertyDtos,
      eventActions: eventActionDtos,
      allowableActions: allowableActions,
      dataSources: dataSources
    });
  }

  // Templates
  const actionTemplates: Template[] = [];
  const eventActionIds: string[] = [];
  const actionPropertyIds: string[] = [];

  for (let eventActionIndex = 0; eventActionIndex < eventMetadata.length; eventActionIndex++) {
    const actionMetadata = eventMetadata[eventActionIndex].allowableActions;
    for (let actionIndex = 0; actionIndex < actionMetadata.length; actionIndex++) {
      const eventActionId = actionMetadata[actionIndex].eventAction.id;

      if (!eventActionIds.includes(eventActionId ?? '')) eventActionIds.push(eventActionId ?? '');

      const actProperties = actionMetadata[actionIndex].actionProperties;
      for (let actPropertiesIndex = 0; actPropertiesIndex < actProperties.length; actPropertiesIndex++) {
        const actionPropertyId = actProperties[actPropertiesIndex].actionProperty.id;

        if (!actionPropertyIds.includes(actionPropertyId ?? '')) actionPropertyIds.push(actionPropertyId ?? '');
      }
    }
  }

  const templates = (await getEventActionTemplates()).data;
  for (let templateIndex = 0; templateIndex < templates.length; templateIndex++) {
    const template = templates[templateIndex];
    const templateEventAction = metadataRes[2].data.find(x => x.id === template.eventActionId);
    const templateEvent = metadataRes[0].data.find(x => x.id === templateEventAction?.eventId);
    const templateAction = metadataRes[4].data.find(x => x.id === templateEventAction?.actionId);

    if (eventActionIds.includes(template.eventActionId ?? '')) {
      actionTemplates.push({
        ...template,
        isDefaultTemplate: false,
        isUserInvite: templateEvent?.name?.toLowerCase() === 'userinvitedevent',
        isAccountInvite: templateEvent?.name?.toLowerCase() === 'accountholderinvitedevent',
        isForgottenPassword: templateEvent?.name?.toLowerCase() === 'forgottenpasswordevent',
        isStatusGroupChange: false,
        isEtaWindowUpdate: false,
        isEmail: templateAction?.name?.toLowerCase() === 'sendemailnotificationaction',
        isSms: templateAction?.name?.toLowerCase() === 'sendsmsnotificationaction',
        isConsolidatedStatusGroupChange: templateEvent?.name?.toLowerCase() === 'activitystatusgroupchangedevent',
        isConsolidatedEtaWindowUpdate: templateEvent?.name?.toLowerCase() === 'activityetachangedevent',
        isCreateButton: false,
        eventDto: templateEvent,
        isStatusChange: false
      });
    }
  }

  const defaultTemplates = (await getDefaultEventActionTemplates()).data;
  for (let defaultTemplateIndex = 0; defaultTemplateIndex < defaultTemplates.length; defaultTemplateIndex++) {
    const defaultTemplate = defaultTemplates[defaultTemplateIndex];
    const defaultTemplateEventAction = metadataRes[2].data.find(x => x.id === defaultTemplate.eventActionId);
    const defaultTemplateEvent = metadataRes[0].data.find(x => x.id === defaultTemplateEventAction?.eventId);
    const defaultTemplateAction = metadataRes[4].data.find(x => x.id === defaultTemplateEventAction?.actionId);

    if (eventActionIds.includes(defaultTemplate.eventActionId ?? '')) {
      actionTemplates.push({
        ...defaultTemplate,
        isDefaultTemplate: true,
        isUserInvite: defaultTemplateEvent?.name?.toLowerCase() === 'userinvitedevent',
        isAccountInvite: defaultTemplateEvent?.name?.toLowerCase() === 'accountholderinvitedevent',
        isForgottenPassword: defaultTemplateEvent?.name?.toLowerCase() === 'forgottenpasswordevent',
        isStatusGroupChange: false,
        isEtaWindowUpdate: false,
        isEmail: defaultTemplateAction?.name?.toLowerCase() === 'sendemailnotificationaction',
        isSms: defaultTemplateAction?.name?.toLowerCase() === 'sendsmsnotificationaction',
        isConsolidatedStatusGroupChange:
          defaultTemplateAction?.name?.toLowerCase() === 'activitystatusgroupchangedevent',
        isConsolidatedEtaWindowUpdate: defaultTemplateAction?.name?.toLowerCase() === 'activityetachangedevent',
        isCreateButton: false,
        eventDto: defaultTemplateEvent,
        isStatusChange: false
      });
    }
  }

  const actionId = metadataRes[4].data.find(x => x.name?.toLowerCase() === 'sendemailnotificationaction')?.id ?? '';

  const accountInviteEvent = metadataRes[0].data.find(x => x.name?.toLowerCase() === 'accountholderinvitedevent');
  const userInviteEvent = metadataRes[0].data.find(x => x.name?.toLowerCase() === 'userinvitedevent');
  const forgottenPasswordEvent = metadataRes[0].data.find(x => x.name?.toLowerCase() === 'forgottenpasswordevent');

  const accountInviteEventAction = metadataRes[2].data.find(
    x => x.eventId === accountInviteEvent?.id && x.actionId === actionId
  );
  const userInviteEventAction = metadataRes[2].data.find(
    x => x.eventId === userInviteEvent?.id && x.actionId === actionId
  );
  const forgottenPasswordEventAction = metadataRes[2].data.find(
    x => x.eventId === forgottenPasswordEvent?.id && x.actionId === actionId
  );

  const accountInviteCategory = ruleProfileCategories.find(x => x.isAccountNotification);
  const userInviteCategory = ruleProfileCategories.find(x => x.isUserNotification);

  const accountInviteProfile = ruleProfiles.find(
    x => x.ruleProfileCategoryId === accountInviteCategory?.id && x.name === `${accountId}-AccountInvitation`
  );
  const accountProfilesToDelete = ruleProfiles.filter(
    x => x.ruleProfileCategoryId === accountInviteCategory?.id && x.name !== `${accountId}-AccountInvitation`
  );
  const accountInviteRule = rules.find(x => x.ruleProfileId === accountInviteProfile?.id);
  let accountInviteTemplate: Template | undefined = undefined;
  if (accountInviteRule?.id) {
    accountInviteTemplate = actionTemplates.find(x => x.eventActionId === accountInviteEventAction?.id);
  } else {
    accountInviteTemplate = actionTemplates.find(
      x => x.eventActionId === accountInviteEventAction?.id && x.isDefaultTemplate
    );
  }

  const accountInvite: AccountInviteDetails = {
    ruleProfileCategory: accountInviteCategory ?? {},
    ruleProfile: accountInviteProfile,
    rule: accountInviteRule,
    template: accountInviteTemplate
  };

  const userInviteProfile = ruleProfiles.find(
    x => x.ruleProfileCategoryId === userInviteCategory?.id && x.name === `${accountId}-UserInvitation`
  );
  const userProfilesToDelete = ruleProfiles.filter(
    x =>
      x.ruleProfileCategoryId === userInviteCategory?.id &&
      x.name !== `${accountId}-UserInvitation` &&
      x.name !== `${accountId}-ForgottenPassword`
  );
  const userInviteRule = rules.find(x => x.ruleProfileId === userInviteProfile?.id);
  let userInviteTemplate: Template | undefined = undefined;
  if (userInviteRule?.id) {
    userInviteTemplate = actionTemplates.find(x => x.eventActionId === userInviteEventAction?.id);
  } else {
    userInviteTemplate = actionTemplates.find(
      x => x.eventActionId === userInviteEventAction?.id && x.isDefaultTemplate
    );
  }

  const userInvite: UserInviteDetails = {
    ruleProfileCategory: userInviteCategory ?? {},
    ruleProfile: userInviteProfile,
    rule: userInviteRule,
    template: userInviteTemplate
  };

  const forgottenPasswordProfile = ruleProfiles.find(
    x => x.ruleProfileCategoryId === userInviteCategory?.id && x.name === `${accountId}-ForgottenPassword`
  );
  const forgottenPasswordRule = rules.find(x => x.ruleProfileId === forgottenPasswordProfile?.id);
  let forgottenPasswordTemplate: Template | undefined = undefined;
  if (forgottenPasswordRule?.id) {
    forgottenPasswordTemplate = actionTemplates.find(x => x.eventActionId === forgottenPasswordEventAction?.id);
  } else {
    forgottenPasswordTemplate = actionTemplates.find(
      x => x.eventActionId === forgottenPasswordEventAction?.id && x.isDefaultTemplate
    );
  }

  const forgottenPassword: ForgottenPasswordDetails = {
    ruleProfileCategory: userInviteCategory ?? {},
    ruleProfile: forgottenPasswordProfile,
    rule: forgottenPasswordRule,
    template: forgottenPasswordTemplate
  };

  return {
    accountInvite: accountInvite,
    userInvite: userInvite,
    forgottenPassword: forgottenPassword,
    propertyTypes: metadataRes[8].data,
    triggerTypes: metadataRes[7].data,
    eventMetadata: eventMetadata,
    emailActionId: emailActionId,
    profilesToDelete: [...accountProfilesToDelete, ...userProfilesToDelete]
  };
};

const accountInformationQueryFn = async (product: Products): Promise<AccountDetailsData> => {
  let accountId = '';

  const [accountResult, accountImageTypeResult, messagingTypesResult] = await Promise.all([
    getMyAccountById(),
    getAccountImageTypes({ filter: `isCompanyLogo eq true` }),
    getMessageTypes({ filter: `isEmail eq true` })
  ]);

  const account = accountResult.data;
  accountId = account.id ?? '';
  const accountImageType = accountImageTypeResult.data[0];
  const messagingTypes = messagingTypesResult.data;

  const accountDetails = getMyAccountDetail();
  const accountImages = getAccountImages({
    filter: `accountImageTypeId eq guid'${accountImageType?.id ?? ''}' and accountId eq guid'${accountId}'`
  });
  const accountSubscriptions = getMyAccountDetailsSubscriptions({
    filter: `accountId eq guid'${accountId}' and isActive eq true`
  });
  const products = getProducts();
  const productLicenceScopes = getProductLicenceScopes();
  const productLicenceTypes = getProductLicenceTypes();
  const messagingServices =
    product === Products.MyPortal
      ? undefined
      : getMessagingServices({
          filter: `messageTypeId eq guid'${messagingTypes[0]?.id ?? ''}' and isDefault eq true`,
          ignoreOwner: true
        });
  const themeData = getAccountUIThemeById(accountId);

  const res = await Promise.allSettled([
    accountDetails,
    accountImages,
    accountSubscriptions,
    products,
    productLicenceScopes,
    productLicenceTypes,
    messagingServices,
    themeData
  ]);

  const accountDetailsResult = res[0].status === 'fulfilled' ? res[0].value : ({} as AxiosResponse<AccountDetailDto>);
  const accountImageResult =
    res[1].status === 'fulfilled' ? res[1].value : ([] as unknown as AxiosResponse<AccountImageDto[]>);
  const accountSubscriptionsResult =
    res[2].status === 'fulfilled' ? res[2].value : ([] as unknown as AxiosResponse<SubscriptionDto[], any>);
  const productsResult =
    res[3].status === 'fulfilled' ? res[3].value : ({ data: [] } as unknown as Awaited<ReturnType<typeof getProducts>>);
  const productLicenceScopesResult =
    res[4].status === 'fulfilled' ? res[4].value : ([] as unknown as AxiosResponse<ProductLicenceScopeDto[], any>);
  const productLicenceTypesResult =
    res[5].status === 'fulfilled' ? res[5].value : ([] as unknown as AxiosResponse<ProductLicenceTypeDto[], any>);
  const messagingServicesResult =
    res[6].status === 'fulfilled' ? res[6].value : ([] as unknown as AxiosResponse<MessagingServiceDto[], any>);
  const themeDataResult = res[7].status === 'fulfilled' ? res[7].value : ({} as AxiosResponse<UithemeDto>);

  let allocatedLicencesInfo: { users: number; deviceAppRegistrations: DeviceAppRegistrationDto[] } | undefined =
    undefined;
  if (
    productsResult.data.find(x => x.productLicenceScopeId !== undefined && x.productLicenceTypeId !== undefined) &&
    product === Products.Flow
  ) {
    // Fetch users and device app registrations
    const userStates = (await getUserStates({ filter: `isActive eq true` })).data;
    const active = userStates.find(x => x.isUserActive);
    const invited = userStates.find(x => x.isInvited);

    const users = getAllUsersAsync({
      filter: `isServiceUser eq false and (userStateId eq guid'${active?.id}' or userStateId eq guid'${invited?.id}')`,
      includeMetadata: true,
      pageNumber: 0,
      pageSize: 1
    });
    const deviceAppRegistrations = getDeviceAppRegistrations();

    const res = await Promise.all([users, deviceAppRegistrations]);

    allocatedLicencesInfo = {
      deviceAppRegistrations: res[1].data,
      users: (res[0].data as ViewDto<UserListItemDto[]>).totalItemCount ?? 0
    };
  }

  return {
    accountInformation: {
      accountHolderUserId: accountDetailsResult?.data.accountHolderUserId,
      address: accountDetailsResult.data.address,
      adminEmailAddress: account.adminEmailAddress,
      adminForename: account.adminForename,
      adminRoleCreated: account.adminRoleCreated,
      adminSurname: account.adminSurname,
      companyRegistrationNumber: accountDetailsResult.data.companyRegistrationNumber,
      defaultCurrencyId: accountDetailsResult.data.defaultCurrencyId,
      email: accountDetailsResult.data.email,
      id: account.id,
      identityProvisioning: account.identityProvisioning,
      invitationSent: account.invitationSent,
      isSubAccount: account.isSubAccount,
      isTenantAccount: account.isTenantAccount,
      maxSalesOrderNumberLength: accountDetailsResult.data.maxSalesOrderNumberLength,
      minSalesOrderNumberLength: accountDetailsResult.data.minSalesOrderNumberLength,
      name: accountDetailsResult.data.name,
      parentAccountId: account.parentAccountId,
      phone: accountDetailsResult.data.phone,
      primaryDepotCode: accountDetailsResult.data.primaryDepotCode,
      provisioningCompleted: account.provisioningCompleted,
      provisioningStarted: account.provisioningStarted,
      isActive: account.isActive,
      startOfFiscalYear: accountDetailsResult.data.startOfFiscalYear,
      subdomain: account.subdomain,
      tenantProvisioned: account.tenantProvisioned,
      tenantProvisioning: account.tenantProvisioning,
      vatnumber: accountDetailsResult.data.vatnumber
    },
    accountImage: accountImageResult.data[0],
    accountImageType: accountImageType,
    messagingServiceId: messagingServicesResult?.data[0]?.id,
    productLicenceScopes: productLicenceScopesResult.data,
    productLicenceTypes: productLicenceTypesResult.data,
    products: productsResult.data,
    subscriptions: accountSubscriptionsResult.data,
    defaultNotifications: undefined,
    theme: themeDataResult?.data ?? {},
    allocatedLicencesInfo: allocatedLicencesInfo
  };
};

/**
 * Default Notifications Query
 */
export const useDefaultNotificationsQuery = (
  accountId: string,
  {
    enabled = true,
    initialData = {},
    onSuccess = undefined,
    refetchOnWindowFocus = false,
    queryKeyOverride
  }: QueryProps<DefaultNotificationsDetails, {}>
): UseQueryResult<DefaultNotificationsDetails> => {
  return useQuery({
    queryKey: queryKeyOverride ?? ['defaultNotifications', accountId],
    queryFn: async () => accountDetailsNotificationsQuery(accountId),
    enabled: enabled && !!accountId,
    initialData,
    refetchOnWindowFocus,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res) })
  });
};

/**
 * Account Information Query
 */
export const getAccountInformationQueryKey = (product: Products, tenantId: string) =>
  ['accountInformation', product, tenantId];

export const useAccountInformationQuery = (
  product: Products,
  {
    enabled = true,
    initialData = {},
    onSuccess = undefined,
    refetchOnWindowFocus = false,
    queryKeyOverride,
    staleTime = 5 * 60 * 1000
  }: QueryProps<AccountDetailsData, {}> & { staleTime?: number }
): UseQueryResult<AccountDetailsData> => {
  return useQuery({
    queryKey: queryKeyOverride ?? ['accountInformation', product],
    queryFn: async () => accountInformationQueryFn(product),
    enabled,
    initialData,
    // The default `initialData` is an empty placeholder ({}), not real data.
    // With a non-zero `staleTime`, React Query would treat that placeholder as
    // fresh and skip the initial fetch entirely, so the Accounts page renders
    // empty and never fires its request. Stamping the initial data as updated
    // at epoch 0 makes it immediately stale, forcing the fetch on mount while
    // still letting `staleTime` throttle subsequent refetches.
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus,
    staleTime,
    ...(typeof onSuccess === 'function' && { onSuccess: res => onSuccess(res) })
  });
};

export const useJeopardyStatusConfigurationsQuery = ({
  accountId,
  onSuccess
}: {
  accountId: string;
  onSuccess: (data: JeopardyStatusConfigurationDto[]) => void;
}) =>
  useQuery({
    queryKey: ['jeopardyStatusConfiguration', accountId],
    queryFn: () => getJeopardyStatusConfigurations({ filter: `ownerTenantId eq guid'${accountId}'` }),
    enabled: !!accountId,
    onSuccess: (response: { data: JeopardyStatusConfigurationDto[] }) => onSuccess(response.data ?? [])
  });
